import { FC, useState } from 'react';
import { navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { ButtonLink, ButtonPrimary, Form, InputText } from '@lambdacurry/component-library';
import { Card, CardContent, Grid } from 'lib/components';
import { otpValidation } from 'lib/forms/validation';
import { CreateUserInput } from 'lib/home/generated/types/api';
import { useFinishRegistrationMutation, useRegisterMutation } from 'lib/home/generated/queries';
import { RegistrationRouteMap, RegistrationStep, useRegistrationStore } from '../../store';
import {
  GettingStartedContent,
  GettingStartedLayout,
  GettingStartedNoCookingFooter,
  RedirectRouter
} from '../../components';
import { default as CheckEmailPattern } from '../../assets/check-email-pattern.svg';
import { useLogin } from '../../hooks';
import * as Yup from 'yup';

const CheckEmail: FC<{ JWT: string }> = ({ JWT }) => {
  const validationSchema = Yup.object().shape(otpValidation);
  const redirectPath = RegistrationRouteMap.customizeBox;
  const login = useLogin();
  const { checkoutInfo, preferences, selectedPlan } = useRegistrationStore();
  const [emailSentStatus, setEmailSentStatus] = useState<'waiting' | 'sending' | 'sent'>('waiting');

  const { mutate: register } = useRegisterMutation({
    onSuccess: () => {
      setEmailSentStatus('sent');
      setTimeout(() => {
        setEmailSentStatus('waiting');
      }, 3000);
    },
    onError: error => {
      if (error.toString().includes('already exists')) navigate('/login');
    }
  });

  const { mutate: finishRegistration, isLoading } = useFinishRegistrationMutation({
    onSuccess: ({ finishRegistration: { token, user } }) => {
      window.dataLayer.push({ event: 'login', user_id: user.id });
      login(token, { redirectPath });
    }
  });

  const handleSubmit = ({ otp }) =>
    finishRegistration(
      { validateAccountInput: { otp, jwt: JWT } },
      {
        onSuccess: data => {
          const { id } = data.finishRegistration.user;
          window.dataLayer.push({ event: 'finish_registration', user_id: id });
        }
      }
    );

  const handleResend = () => {
    setEmailSentStatus('sending');
    register({
      createUserInput: {
        ...checkoutInfo,
        meal_preferences: preferences,
        selectedPlan
      } as CreateUserInput
    });
  };

  return (
    <GettingStartedLayout step={RegistrationStep.checkEmail}>
      <GettingStartedContent>
        <Grid>
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <Card className="relative">
              <div className="lg:flex">
                <CardContent>
                  <h2>Check Your Email</h2>
                  <p>Please copy the code in your email to finish setting up your subscription.</p>

                  <ButtonLink
                    className="!underline !h-[21px] lg:mb-2"
                    onClick={handleResend}
                    disabled={['sending', 'sent'].includes(emailSentStatus)}
                  >
                    Haven't received an email?{' '}
                    {emailSentStatus === 'waiting'
                      ? 'Resend it now'
                      : emailSentStatus === 'sending'
                      ? 'Sending...'
                      : 'Check your email'}
                  </ButtonLink>

                  <Form initialValues={{ otp: '' }} onSubmit={handleSubmit} validationSchema={validationSchema}>
                    {formikProps => {
                      const { submitForm, validateForm } = formikProps;
                      const autoSubmit = async (otp: string) => (await validateForm({ otp })) && submitForm();

                      return (
                        <>
                          <InputText
                            data-cy="one-time-password"
                            name="otp"
                            onChange={event => autoSubmit(event.target.value)}
                            placeholder="######"
                            type="phone"
                          />
                          <ButtonPrimary
                            className="mt-2 lg:mt-4 w-full md:w-56"
                            data-cy="submit"
                            disabled={isLoading}
                            type="submit"
                          >
                            {isLoading ? 'Loading' : 'Continue'}
                          </ButtonPrimary>
                        </>
                      );
                    }}
                  </Form>
                </CardContent>
                <CardContent className="bg-brand-yellow-100 min-h-[120px] lg:min-h-0 lg:min-w-[380px] lg:max-w-md pr-24 lg:pr-0 !border-0" />
              </div>

              <StaticImage
                className="!absolute bottom-0 right-0 !hidden lg:!block h-48 lg:h-[410px]"
                src="../../assets/check-email-graphic.png"
                alt="planted herbs"
                placeholder="tracedSVG"
                height={410}
                quality={100}
              />
              <StaticImage
                className="!absolute bottom-0 right-0 lg:!hidden"
                src="../../assets/check-email-graphic.png"
                alt="planted herbs"
                placeholder="tracedSVG"
                height={188}
              />
              <CheckEmailPattern className="lg:w-full lg:h-auto bg-brand-yellow-100" />
            </Card>
          </div>
        </Grid>
      </GettingStartedContent>

      <GettingStartedNoCookingFooter />
    </GettingStartedLayout>
  );
};

const CheckEmailPage: FC<{ location?: { state: { JWT: string } } }> = ({ location }) => (
  <RedirectRouter
    component={<CheckEmail JWT={location?.state?.JWT} />}
    location={location}
    to={RegistrationRouteMap.createAccount}
  />
);

export default CheckEmailPage;
